// Display formatting. Lamports stay integers until this edge; SOL strings
// are produced with BigInt division by 1e9, never float arithmetic, so
// string-typed big integers (> 2^53) format exactly.

const LAMPORTS_PER_SOL = 1_000_000_000n;

/** "1234567" -> "1,234,567". Input must be a plain decimal integer string. */
export function groupDigits(digits: string): string {
  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

function toBigInt(v: number | string | bigint): bigint | null {
  if (typeof v === "bigint") {
    return v;
  }
  if (typeof v === "number") {
    return Number.isSafeInteger(v) ? BigInt(v) : null;
  }
  return /^-?[0-9]+$/.test(v) ? BigInt(v) : null;
}

/** Lamports -> exact SOL string ("1.5", "0.000005"). Null/unparseable -> em dash. */
export function lamportsToSol(v: number | string | bigint | null): string {
  if (v === null) {
    return "—";
  }
  const b = toBigInt(v);
  if (b === null) {
    return "—";
  }
  const neg = b < 0n;
  const abs = neg ? -b : b;
  const whole = abs / LAMPORTS_PER_SOL;
  const frac = abs % LAMPORTS_PER_SOL;
  const fracStr =
    frac === 0n ? "" : `.${frac.toString().padStart(9, "0").replace(/0+$/, "")}`;
  return `${neg ? "-" : ""}${groupDigits(whole.toString())}${fracStr}`;
}

const MICRO_PER_CENT = 10_000n;

/**
 * Integer micro-USD -> dollars with exactly two decimals ("$210.00",
 * "-$0.01"). Pure BigInt arithmetic: rounds to the nearest cent, half away
 * from zero, with digit grouping on the whole-dollar part — no float
 * accumulation at any step. Null/unparseable -> em dash, never $0.00.
 */
export function formatUsdMicro(v: number | string | bigint | null): string {
  if (v === null) {
    return "—";
  }
  const b = toBigInt(v);
  if (b === null) {
    return "—";
  }
  const abs = b < 0n ? -b : b;
  const cents = (abs + MICRO_PER_CENT / 2n) / MICRO_PER_CENT;
  // Sign only when the rounded value is nonzero: "-$0.00" is a directional
  // claim the displayed precision cannot back.
  const neg = b < 0n && cents !== 0n;
  const whole = cents / 100n;
  const frac = cents % 100n;
  return `${neg ? "-" : ""}$${groupDigits(whole.toString())}.${frac.toString().padStart(2, "0")}`;
}

/** Integer lamports with digit grouping. Null -> em dash. */
export function formatLamports(v: number | string | bigint | null): string {
  if (v === null) {
    return "—";
  }
  const b = toBigInt(v);
  if (b === null) {
    return "—";
  }
  const neg = b < 0n;
  const abs = neg ? -b : b;
  return `${neg ? "-" : ""}${groupDigits(abs.toString())}`;
}

/** Percentage with fixed decimals and unit. Null -> em dash. */
export function formatPct(v: number | null, decimals = 2): string {
  return v === null ? "—" : `${v.toFixed(decimals)} %`;
}

/** Signed percentage-point delta: "+0.14 pp". Null -> em dash. */
export function formatSignedPp(v: number | null, decimals = 2): string {
  if (v === null) {
    return "—";
  }
  const sign = v >= 0 ? "+" : "";
  return `${sign}${v.toFixed(decimals)} pp`;
}

/** Plain count with grouping. Null -> em dash. */
export function formatCount(v: number | null): string {
  return v === null ? "—" : groupDigits(Math.trunc(v).toString());
}

function pad2(n: number): string {
  return n.toString().padStart(2, "0");
}

/** Unix seconds -> "YYYY-MM-DD HH:MM UTC". Null -> em dash. */
export function formatUtcDateTime(unixSecs: number | null): string {
  if (unixSecs === null) {
    return "—";
  }
  const d = new Date(unixSecs * 1000);
  return (
    `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())} ` +
    `${pad2(d.getUTCHours())}:${pad2(d.getUTCMinutes())} UTC`
  );
}

/** Unix seconds -> "HH:MM" (UTC — the blocks datastream time column). Null -> em dash. */
export function formatUtcHourMinute(unixSecs: number | null): string {
  if (unixSecs === null) {
    return "—";
  }
  const d = new Date(unixSecs * 1000);
  return `${pad2(d.getUTCHours())}:${pad2(d.getUTCMinutes())}`;
}

/** Date -> "YYYY-MM-DD" (UTC). */
export function formatUtcDate(d: Date): string {
  return `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}`;
}

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
] as const;

/** Unix seconds -> "Jul 10" (UTC, no year — used for the header/caption as-of stamp). */
export function formatUtcMonthDay(unixSecs: number | null): string {
  if (unixSecs === null) {
    return "—";
  }
  const d = new Date(unixSecs * 1000);
  return `${MONTHS[d.getUTCMonth()]} ${d.getUTCDate()}`;
}

/** Unix seconds -> "Jul 8 05:12" (UTC, no year — used for tooltip/pin epoch windows). */
export function formatUtcMonthDayTime(unixSecs: number | null): string {
  if (unixSecs === null) {
    return "—";
  }
  const d = new Date(unixSecs * 1000);
  return `${MONTHS[d.getUTCMonth()]} ${d.getUTCDate()} ${pad2(d.getUTCHours())}:${pad2(d.getUTCMinutes())}`;
}

/**
 * Signed delta as prose, one grammar for every delta in the widget:
 * "0.31 pp above cluster p50" / "0.31 pp below cluster p50" / "0.00 pp at
 * cluster p50" (exact equality). Formula: delta = ours - p50, same epoch;
 * never rendered as an unsigned magnitude regardless of direction.
 */
export function formatDeltaWords(deltaPp: number | null, decimals = 2): string {
  if (deltaPp === null) {
    return "delta unavailable";
  }
  const abs = Math.abs(deltaPp).toFixed(decimals);
  if (Number.parseFloat(abs) === 0) {
    // Sign is not meaningful at the displayed precision: no directional claim.
    const eps = 0.5 * Math.pow(10, -decimals);
    return `matches cluster p50 (within ${eps} pp)`;
  }
  if (deltaPp > 0) {
    return `${abs} pp above cluster p50`;
  }
  return `${abs} pp below cluster p50`;
}

/**
 * Signed basis-points delta: bps = pp * 100 (bps is the audience's native
 * institutional unit; 1 pp = 100 bps). Integer when the rounded value is
 * whole, one decimal
 * otherwise: "+28 bps", "+12.5 bps". Null -> em dash.
 */
export function formatBps(deltaPp: number | null): string {
  if (deltaPp === null) {
    return "—";
  }
  const bps = deltaPp * 100;
  const sign = bps >= 0 ? "+" : "";
  return `${sign}${formatBpsMagnitude(bps)} bps`;
}

/** Rounds to one decimal first (absorbs float noise from `* 100`), then drops the decimal when the rounded value is whole. */
function formatBpsMagnitude(bps: number): string {
  const rounded = Math.round(bps * 10) / 10;
  return Number.isInteger(rounded) ? rounded.toFixed(0) : rounded.toFixed(1);
}

/**
 * Absolute bps gap phrased as a positive tightness claim, not a ranking:
 * "within 11.0 bps" — always one decimal (unlike formatBps) because at this
 * cell the whole point is that a tiny, honestly-stated gap (a fraction of a
 * cent per 1,000 SOL/yr) reads as "essentially tied," not "N validators
 * ahead of you." Caller only invokes this with gapPp > 0 (see
 * formatAboveBestBps for the ours-exceed-max case); no clamping here.
 */
/** Below ~1e-4 bps a gap is finer than anything the served figures resolve. */
const BOUND_MAX_DECIMALS = 4;

/**
 * A gap rendered as an upper BOUND: escalating precision, never rounded down.
 *
 * "within X" asserts gap <= X, so rounding to nearest can state a bound the
 * measurement contradicts. A real 0.0023 bps gap rendered "within 0.0 bps" —
 * claiming a parity with the cluster best that the exact-tie branch ("at
 * cluster best") exists to state, and claiming it in our own favour, which is
 * exactly the shape of a number quoted back at the operator. This codebase
 * already forbids the pattern twice: skipRateValueText's "< 0.1%", and
 * formatEconomicGapApprox's "< 0.01 SOL / 1k SOL / yr" — the sibling line on
 * the very same cell. This formatter was the one that skipped it.
 *
 * Precision escalates to the first decimal place where the gap is non-zero
 * (bandRangeText's degeneracy-adaptive precedent), then the bound rounds UP.
 * The statement is then true AND a genuinely tiny gap reads as tiny, rather
 * than as a lie ("0.0") or as a shrug ("< 0.1", ~40x looser than measured).
 *
 * The epsilon absorbs float noise so an exact 11.0 bps cannot ceil to 11.1.
 */
function boundUpBps(bps: number): string {
  if (bps <= 0) {
    return "0.0";
  }
  let decimals = 1;
  while (decimals < BOUND_MAX_DECIMALS && bps < 0.5 * 10 ** -decimals) {
    decimals += 1;
  }
  const factor = 10 ** decimals;
  return (Math.ceil(bps * factor - 1e-9) / factor).toFixed(decimals);
}

export function formatWithinBps(gapPp: number | null): string {
  if (gapPp === null) {
    return "—";
  }
  return `within ${boundUpBps(gapPp * 100)} bps`;
}

/** Rounds `v` to `sig` significant figures, returned as a plain decimal string (no exponential notation). */
function toSignificantFigures(v: number, sig: number): string {
  if (v === 0) {
    return "0";
  }
  const magnitude = Math.floor(Math.log10(Math.abs(v)));
  const factor = Math.pow(10, sig - 1 - magnitude);
  const rounded = Math.round(v * factor) / factor;
  // Guard against floating-point exponential notation on very small magnitudes.
  const decimals = Math.max(0, sig - 1 - magnitude);
  return rounded.toFixed(Math.min(decimals, 20));
}

/** Below this SOL magnitude, 2-decimal (or 2-sig-fig) display would imply false precision; state the honest bound instead. */
const ECONOMIC_FLOOR_SOL = 0.01;

/**
 * Economic equivalent of a signed pp delta, SOL per 1,000 SOL staked per
 * year: delta_pp / 100 * 1000 = delta_pp * 10 (pure arithmetic on two
 * already-served numbers). Signed, 2 decimals, compact unit:
 * "+2.84 SOL / 1k SOL / yr". A
 * real nonzero value below the floor never rounds to "+0.00" (false
 * precision) — it renders the honest bound "< 0.01 SOL / 1k SOL / yr"
 * instead (no ~, no sign).
 */
export function formatEconomicDeltaSigned(deltaPp: number | null): string {
  if (deltaPp === null) {
    return "—";
  }
  const sol = deltaPp * 10;
  if (sol !== 0 && Math.abs(sol) < ECONOMIC_FLOOR_SOL) {
    return "< 0.01 SOL / 1k SOL / yr";
  }
  const sign = sol >= 0 ? "+" : "";
  return `${sign}${sol.toFixed(2)} SOL / 1k SOL / yr`;
}

/**
 * Economic equivalent of an absolute (unsigned) pp gap, "~"-prefixed
 * because it is a rounded restatement (2 significant figures) of the
 * served delta, not the served value itself: "~1.1 SOL / 1k SOL / yr".
 * Same delta_pp * 10 arithmetic and floor as formatEconomicDeltaSigned:
 * below the floor, 2-sig-fig rounding would understate to something like
 * "~0.0014", so the honest bound replaces it: "< 0.01 SOL / 1k SOL / yr".
 */
export function formatEconomicGapApprox(gapPp: number | null): string {
  if (gapPp === null) {
    return "—";
  }
  const sol = Math.abs(gapPp) * 10;
  if (sol < ECONOMIC_FLOOR_SOL) {
    return "< 0.01 SOL / 1k SOL / yr";
  }
  return `~${toSignificantFigures(sol, 2)} SOL / 1k SOL / yr`;
}

/**
 * Positive-surplus counterpart to formatWithinBps: ours exceeds the served
 * cluster max, stated in bps with the same "above best" wording as the
 * cell's qualitative label ("+11 bps above best") — the paired economic
 * SOL detail renders separately, via formatEconomicDeltaSigned. Signed
 * throughout — never clamped.
 */
export function formatAboveBestBps(deltaPp: number | null): string {
  if (deltaPp === null) {
    return "—";
  }
  return `${formatBps(deltaPp)} above best`;
}
